import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { fetchAuthSession } from "./auth-session-request";
import { safeReturnUrl } from "./origins";

export type AuthUser = {
  id: string;
  name: string;
  email: string;
  emailVerified?: boolean;
  image?: string | null;
  twoFactorEnabled?: boolean;
};

type SessionResponse = {
  user?: AuthUser | null;
};

type AuthState = {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
  signin: (returnTo?: string) => Promise<void>;
  signinRedirect: (returnTo?: string) => Promise<void>;
  signout: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

function currentReturnPath(): string {
  if (typeof window === "undefined") return "/";
  return window.location.href;
}

export function LocalAuthProvider({ children }: { children: ReactNode }) {
  // The public header must immediately represent the real session state. A
  // deferred request made signed-in visitors look anonymous until their first
  // pointer or keyboard interaction, which made the account entry ambiguous.
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    void fetchAuthSession(controller.signal)
      .then(async (response) => {
        if (!response.ok) throw new Error("Sessione non disponibile");
        const session = (await response.json()) as SessionResponse | null;
        setUser(session?.user ?? null);
      })
      .catch((caught) => {
        if (caught instanceof DOMException && caught.name === "AbortError")
          return;
        setError(caught instanceof Error ? caught : new Error(String(caught)));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  const signin = useCallback(async (requestedReturnTo?: string) => {
    const returnTo = safeReturnUrl(requestedReturnTo ?? currentReturnPath());
    window.location.assign(`/login?returnTo=${encodeURIComponent(returnTo)}`);
  }, []);

  const signout = useCallback(async () => {
    await fetch("/api/auth/sign-out", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: "{}",
    });
    window.location.assign("/");
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      isLoading,
      error,
      signin,
      signinRedirect: signin,
      signout,
    }),
    [error, isLoading, signin, signout, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error("useAuth deve essere usato dentro LocalAuthProvider");
  }
  return value;
}

export function useUser() {
  const { user, isLoading, error } = useAuth();
  return { user, isLoading, error };
}
